/**
 * CR320: Layout-Driven Deal Sorting
 * 
 * Implements sort options defined in layout_json.sortOptions.
 * Each sort option has a specific behavior:
 * 
 * - newest: Most recently published/updated deals first
 * - best_value: Highest effective_percent_back first
 * - expiring_soon: Deals expiring soonest first (nulls last)
 * - merchant_az: Alphabetical by merchant name
 * - category_az: Alphabetical by category
 */

import type { Deal } from "@/lib/types/deals";
import type { SortOptionId } from "@/lib/layout-studio/types";
import { computeEffectivePercentBack, computeDealValueCad, computeDealStatusForSort } from "@/lib/types/deals";

/**
 * Sort deals by the specified sort option.
 */
export function sortDealsByOption(deals: Deal[], sortOption: SortOptionId): Deal[] {
  return [...deals].sort((a, b) => {
    switch (sortOption) {
      case "newest":
        return sortByNewest(a, b);
      
      case "best_value":
        return sortByBestValue(a, b);
      
      case "expiring_soon":
        return sortByExpiringSoon(a, b);
      
      case "merchant_az":
        return sortByMerchantAZ(a, b);
      
      case "category_az":
        return sortByCategoryAZ(a, b);
      
      default:
        // Fallback to newest
        return sortByNewest(a, b);
    }
  });
}

/**
 * Sort by newest first (most recently published/updated)
 * Uses end_date as proxy for "freshness" if available
 */
function sortByNewest(a: Deal, b: Deal): number {
  // Status order: active (0) -> upcoming (1) -> expired (2)
  const statusA = computeDealStatusForSort(a);
  const statusB = computeDealStatusForSort(b);
  if (statusA !== statusB) return statusA - statusB;
  
  // Then by end_date descending (most distant end = newest deal)
  const endA = a.end_date ? new Date(a.end_date).getTime() : 0;
  const endB = b.end_date ? new Date(b.end_date).getTime() : 0;
  if (endB !== endA) return endB - endA;
  
  // Then by start_date descending
  const startA = a.start_date ? new Date(a.start_date).getTime() : 0;
  const startB = b.start_date ? new Date(b.start_date).getTime() : 0;
  if (startB !== startA) return startB - startA;
  
  // Fallback: merchant name
  return (a.merchant || "").localeCompare(b.merchant || "");
}

/**
 * Sort by best value first (highest effective_percent_back)
 * Status-prioritized: active/upcoming first, then expired
 */
function sortByBestValue(a: Deal, b: Deal): number {
  // Status order: active (0) -> upcoming (1) -> expired (2)
  const statusA = computeDealStatusForSort(a);
  const statusB = computeDealStatusForSort(b);
  if (statusA !== statusB) return statusA - statusB;
  
  // Then by effective_percent_back descending
  const percentA = computeEffectivePercentBack(a) ?? -1;
  const percentB = computeEffectivePercentBack(b) ?? -1;
  if (percentB !== percentA) return percentB - percentA;
  
  // Then by computed_value_cad descending
  const valueA = computeDealValueCad(a) ?? -1;
  const valueB = computeDealValueCad(b) ?? -1;
  if (valueB !== valueA) return valueB - valueA;
  
  // Then alphabetically by merchant
  return (a.merchant || "").toLowerCase().localeCompare((b.merchant || "").toLowerCase());
}

/**
 * Sort by expiring soon (closest end_date first)
 * Nulls (no end date) go last
 */
function sortByExpiringSoon(a: Deal, b: Deal): number {
  // Status order: active (0) -> upcoming (1) -> expired (2)
  const statusA = computeDealStatusForSort(a);
  const statusB = computeDealStatusForSort(b);
  if (statusA !== statusB) return statusA - statusB;
  
  // Then by end_date ascending (soonest first), nulls last
  const endA = a.end_date ? new Date(a.end_date).getTime() : Infinity;
  const endB = b.end_date ? new Date(b.end_date).getTime() : Infinity;
  if (endA !== endB) return endA - endB;
  
  // Fallback: best value
  const percentA = computeEffectivePercentBack(a) ?? -1;
  const percentB = computeEffectivePercentBack(b) ?? -1;
  return percentB - percentA;
}

/**
 * Sort alphabetically by merchant name (A-Z)
 */
function sortByMerchantAZ(a: Deal, b: Deal): number {
  const merchantA = (a.promo_merchant_name || a.merchant || "").toLowerCase();
  const merchantB = (b.promo_merchant_name || b.merchant || "").toLowerCase();
  const cmp = merchantA.localeCompare(merchantB);
  if (cmp !== 0) return cmp;
  
  // Secondary: best value
  const percentA = computeEffectivePercentBack(a) ?? -1;
  const percentB = computeEffectivePercentBack(b) ?? -1;
  return percentB - percentA;
}

/**
 * Sort alphabetically by category (A-Z)
 * Uses deal_category if available
 */
function sortByCategoryAZ(a: Deal, b: Deal): number {
  const catA = (a.deal_category || "UNCLASSIFIED").toLowerCase();
  const catB = (b.deal_category || "UNCLASSIFIED").toLowerCase();
  const cmp = catA.localeCompare(catB);
  if (cmp !== 0) return cmp;
  
  // Secondary: merchant name
  return (a.merchant || "").toLowerCase().localeCompare((b.merchant || "").toLowerCase());
}
